import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, of } from 'rxjs';
import { catchError, map, retryWhen, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// Response interfaces
export interface BriaShadowResponse {
  result_url: string;
}

// Raw API response - result is array of [url, seed, session_id] tuples
export interface BriaLifestyleShotRawResponse {
  result: [string, number, string][];
}

// Normalized response for our use
export interface BriaLifestyleShotResponse {
  result_url: string;
  seed?: number;
  session_id?: string;
}

export interface BriaErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

// Trial tracking
export interface BriaTrialStatus {
  used: number;
  limit: number;
  remaining: number;
  isTrialActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BriaAiService {
  // Call Bria API directly - they support CORS for browser requests
  private readonly baseUrl = 'https://engine.prod.bria-api.com/v1';
  private readonly TRIAL_LIMIT = 9999; // TODO: Change back to 10 for production
  private readonly STORAGE_KEY = 'artflow-bria-trial';

  constructor(private http: HttpClient) {}

  /**
   * Get HTTP headers with API token
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'api_token': environment.briaApiKey || ''
    });
  }

  /**
   * Check if input is a base64 data URL
   */
  private isBase64DataUrl(input: string): boolean {
    return input.startsWith('data:');
  }

  /**
   * Strip data URL prefix from base64 string
   * Bria API requires raw base64 without 'data:image/png;base64,' prefix
   */
  private stripBase64Prefix(dataUrl: string): string {
    const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
    if (base64Match) {
      return base64Match[1];
    }
    return dataUrl;
  }

  /**
   * Build image payload - uses 'file' for base64 or 'image_url' for URLs
   */
  private buildImagePayload(imageInput: string): { file?: string; image_url?: string } {
    if (this.isBase64DataUrl(imageInput)) {
      return { file: this.stripBase64Prefix(imageInput) };
    }
    return { image_url: imageInput };
  }

  /**
   * Get current trial status for the user
   * Uses localStorage for MVP - can be migrated to backend later
   */
  getTrialStatus(userId: string): BriaTrialStatus {
    const key = `${this.STORAGE_KEY}-${userId}`;
    const stored = localStorage.getItem(key);
    const used = stored ? parseInt(stored, 10) : 0;

    return {
      used,
      limit: this.TRIAL_LIMIT,
      remaining: Math.max(0, this.TRIAL_LIMIT - used),
      isTrialActive: used < this.TRIAL_LIMIT
    };
  }

  /**
   * Increment trial usage count
   */
  private incrementTrialUsage(userId: string): void {
    const key = `${this.STORAGE_KEY}-${userId}`;
    const stored = localStorage.getItem(key);
    const current = stored ? parseInt(stored, 10) : 0;
    localStorage.setItem(key, (current + 1).toString());
  }

  /**
   * Check if user can use AI background feature
   */
  canUseAiBackground(userId: string): boolean {
    return this.getTrialStatus(userId).isTrialActive;
  }

  /**
   * Add realistic shadow to a cutout image
   * @param imageUrl URL or base64 of the image with transparent background
   * @param options Shadow configuration options
   *
   * shadow_type options:
   * - 'regular': Drop shadow (offset from object)
   * - 'float': Elliptical shadow under the object (like sitting on a surface)
   */
  addShadow(
    imageInput: string,
    options: {
      shadowType?: 'regular' | 'float';
      intensity?: number;
      blur?: number;
      position?: [number, number];
      // Float shadow specific options
      width?: number;
      height?: number;
      color?: string;
    } = {}
  ): Observable<BriaShadowResponse> {
    const url = `${this.baseUrl}/product/shadow`;

    // Build proper payload - use 'file' for base64, 'image_url' for URLs
    const imagePayload = this.buildImagePayload(imageInput);

    // Use 'float' type by default for realistic surface shadows
    const shadowType = options.shadowType || 'float';

    const body: Record<string, unknown> = {
      ...imagePayload,
      shadow_type: shadowType,
      shadow_intensity: options.intensity ?? 70,
      shadow_blur: options.blur ?? (shadowType === 'float' ? 20 : 15),
    };

    // Add position if provided
    if (options.position) {
      body['shadow_position'] = options.position;
    }

    // Add float-specific options
    if (shadowType === 'float') {
      if (options.width !== undefined) {
        body['shadow_width'] = options.width;
      }
      if (options.height !== undefined) {
        body['shadow_height'] = options.height;
      }
    }

    // Add shadow color if provided
    if (options.color) {
      body['shadow_color'] = options.color;
    }

    // Debug logging
    const isBase64 = this.isBase64DataUrl(imageInput);
    console.log('Bria Shadow API Request:', {
      url,
      inputType: isBase64 ? 'base64' : 'url',
      inputLength: imageInput?.length,
      inputPrefix: imageInput?.substring(0, 50),
      bodyKeys: Object.keys(body)
    });

    return this.http.post<BriaShadowResponse>(url, body, { headers: this.getHeaders() })
      .pipe(
        this.handleRetry<BriaShadowResponse>(),
        catchError(this.handleError)
      );
  }

  /**
   * Generate a lifestyle background from text prompt
   * @param imageUrl URL or base64 of the cutout image
   * @param sceneDescription Text description of the desired background
   * @param userId User ID for trial tracking
   */
  generateLifestyleShot(
    imageInput: string,
    sceneDescription: string,
    userId: string
  ): Observable<BriaLifestyleShotResponse> {
    // Check trial status first
    if (!this.canUseAiBackground(userId)) {
      return throwError(() => new Error('Trial limit reached. Please upgrade to continue using AI backgrounds.'));
    }

    const url = `${this.baseUrl}/product/lifestyle_shot_by_text`;

    // Build proper payload - use 'file' for base64, 'image_url' for URLs
    const imagePayload = this.buildImagePayload(imageInput);

    const body = {
      ...imagePayload,
      scene_description: sceneDescription,
      placement_type: 'original',
      num_results: 1,
      optimize_description: true,
      sync: true
    };

    // Debug logging
    const isBase64 = this.isBase64DataUrl(imageInput);
    console.log('Bria Lifestyle Shot API Request:', {
      url,
      inputType: isBase64 ? 'base64' : 'url',
      inputLength: imageInput?.length,
      sceneDescription,
      bodyKeys: Object.keys(body)
    });

    return this.http.post<BriaLifestyleShotRawResponse>(url, body, { headers: this.getHeaders() })
      .pipe(
        tap((rawResponse) => {
          // Log raw response for debugging
          console.log('Bria Lifestyle Shot Raw Response:', rawResponse);
        }),
        map((rawResponse: BriaLifestyleShotRawResponse): BriaLifestyleShotResponse => {
          // Transform result array to normalized response
          // Result format: [[url, seed, session_id], ...]
          if (!rawResponse.result || rawResponse.result.length === 0) {
            throw new Error('No results returned from AI background generation');
          }
          const firstResult = rawResponse.result[0];
          if (!firstResult || !firstResult[0]) {
            throw new Error('Invalid result format from AI background generation');
          }
          return {
            result_url: firstResult[0],
            seed: firstResult[1],
            session_id: firstResult[2]
          };
        }),
        tap(() => {
          // Only increment on success after transformation
          this.incrementTrialUsage(userId);
        }),
        this.handleRetry<BriaLifestyleShotResponse>(),
        catchError(this.handleError)
      );
  }

  /**
   * Generate lifestyle shot with optional shadow pre-step
   * This is the main method to use for the complete workflow
   */
  generateWithShadow(
    imageUrl: string,
    sceneDescription: string,
    userId: string,
    addShadow: boolean = true,
    shadowOptions?: {
      shadowType?: 'regular' | 'float';
      intensity?: number;
      blur?: number;
      position?: [number, number];
      width?: number;
      height?: number;
    }
  ): Observable<BriaLifestyleShotResponse> {
    // Check trial status first
    if (!this.canUseAiBackground(userId)) {
      return throwError(() => new Error('Trial limit reached. Please upgrade to continue using AI backgrounds.'));
    }

    if (!addShadow) {
      // Skip shadow, go directly to lifestyle shot
      return this.generateLifestyleShot(imageUrl, sceneDescription, userId);
    }

    // Add realistic float shadow first, then generate lifestyle shot
    // Float shadow creates an elliptical shadow under the product (like sitting on a surface)
    return this.addShadow(imageUrl, shadowOptions || { shadowType: 'float' }).pipe(
      mergeMap(shadowResult => {
        return this.generateLifestyleShot(shadowResult.result_url, sceneDescription, userId);
      })
    );
  }

  /**
   * Convert canvas/image to base64 data URL for API submission
   */
  imageToBase64(canvas: HTMLCanvasElement): string {
    return canvas.toDataURL('image/png');
  }

  /**
   * Download image from URL (since Bria URLs expire after 1 hour)
   * Returns as HTMLImageElement for use in canvas
   */
  downloadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(new Error('Failed to download generated image'));
      img.src = url;
    });
  }

  /**
   * Retry logic for transient failures
   */
  private handleRetry<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        retryWhen(errors =>
          errors.pipe(
            mergeMap((error: HttpErrorResponse, index) => {
              const retryAttempt = index + 1;
              // Only retry on 500 errors, max 1 retry
              if (retryAttempt > 1 || error.status !== 500) {
                return throwError(() => error);
              }
              // Wait 2 seconds before retry
              return timer(2000);
            })
          )
        )
      );
  }

  /**
   * Error handler with user-friendly messages
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let userMessage = 'An error occurred while generating the background.';

    // Log full error for debugging
    console.error('Bria API Error:', {
      status: error.status,
      statusText: error.statusText,
      error: error.error,
      message: error.message
    });

    switch (error.status) {
      case 400:
        userMessage = error.error?.message || error.error?.detail || 'Invalid request. Please check your image and prompt.';
        break;
      case 401:
        userMessage = 'Authentication failed. Please contact support.';
        console.error('Bria API authentication failed - check API key configuration');
        break;
      case 413:
        userMessage = 'Image is too large. Please use an image under 12MB.';
        break;
      case 422:
        userMessage = error.error?.message || error.error?.detail || 'The image was rejected. Please try a different image.';
        break;
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 460:
        // Bria custom error - likely validation or quota issue
        userMessage = error.error?.message || error.error?.detail || error.error?.error || 'Request validation failed. Please try a different image or prompt.';
        break;
      case 500:
        userMessage = 'Server error. Please try again in a moment.';
        break;
      default:
        if (error.error?.message) {
          userMessage = error.error.message;
        } else if (error.error?.detail) {
          userMessage = error.error.detail;
        } else if (error.error?.error) {
          userMessage = error.error.error;
        }
    }

    return throwError(() => new Error(userMessage));
  };

  /**
   * Scene description presets for quick selection
   */
  readonly scenePresets = [
    { name: 'Studio White', prompt: 'Clean white studio background with soft professional lighting' },
    { name: 'Natural Light', prompt: 'Natural daylight streaming through window, soft shadows on neutral surface' },
    { name: 'Marble Surface', prompt: 'Elegant white marble surface with subtle veining, minimalist setting' },
    { name: 'Wood Table', prompt: 'Warm rustic wooden table surface with natural grain, cozy ambient lighting' },
    { name: 'Outdoor Garden', prompt: 'Lush green garden setting with soft bokeh, natural sunlight' },
    { name: 'Modern Interior', prompt: 'Contemporary minimalist interior, clean lines, neutral tones' },
    { name: 'Cozy Home', prompt: 'Warm cozy living room setting with soft textiles and warm lighting' },
    { name: 'Art Gallery', prompt: 'Modern art gallery white wall, professional museum lighting' },
  ];
}
